import { ComponentRef, EventEmitter } from '@angular/core';
import { dynamicCollectionRendererKey } from '@epicuro-next/constants/dynamic-elements';
import { IDataProvidersResolver } from '@epicuro-next/data-providers/providers-registry';
import {
  DataContainer,
  HasComponentKeyType,
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
  isIDynamicBindableValue,
  isIDynamicBindableValueCombination,
  isIDynamicBindableValueOptions,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { WHITELIST_COMPONENTS_INPUTS } from '@epicuro-next/platform/dynamic-components-registrator';
import {
  DynamicLocalePipe,
  LOCALE_PREFIX_REGEXP,
} from '@epicuro-next/platform/i18n';
import { getValue } from '@epicuro-next/utilities';
import {
  catchError,
  combineLatest,
  concat,
  filter,
  isObservable,
  map,
  mergeMap,
  Observable,
  of,
  pairwise,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';

import { ComponentsRelationshipService } from '../services/components-relationship.service';
import { IdProviderService } from '../services/id-provider.service';

import { resolveDynamicBindableValuesFromDataProvidersResolver } from './resolve-dynamic-bindable-values';

enum RelativeProvider {
  parent = 'parent',
  self = 'self',
  selfParent = 'self-parent',
}

type IDynamicBindableValueWithRelativeProvider = IDynamicBindableValue & {
  provider: RelativeProvider;
  property: string;
};

export type ErrorContainer = { __resolveValueError__: unknown };
export type ErrorCallback = (errorContainer: ErrorContainer) => void;
export type ErrorCallbacks = { [key: string]: ErrorCallback };

export type ResolveValueOptions = {
  overrideWhitelistCheck?: boolean;
  errorCallback?: ErrorCallback;
};

export type ResolveValuesOptions = {
  overrideWhitelistCheck?: boolean;
  errorCallbacks?: ErrorCallbacks;
};

function isUsingRelativeProvider(
  dynamicBindableValue: IDynamicBindableValue,
): dynamicBindableValue is IDynamicBindableValueWithRelativeProvider {
  const values = Object.values(RelativeProvider);
  return values.some((value) => value === dynamicBindableValue.provider);
}

const idProviderPrefixSeparator = ':';
const idProviderPrefix = 'id';

export function isUsingIdProvider(provider: string): boolean {
  provider = provider.toLowerCase().trim();
  return (
    provider.startsWith(idProviderPrefix) &&
    provider.indexOf(idProviderPrefixSeparator) >= idProviderPrefix.length
  );
}

export function getIdFromProvider(idProvider: string): string {
  const indexOfSeparator = idProvider.indexOf(idProviderPrefixSeparator);
  const provider = idProvider.substring(indexOfSeparator + 1);
  return provider.trim();
}

function componentIsWhiteListedToResolveInputsDirectly(
  componentKey: string,
): boolean {
  return WHITELIST_COMPONENTS_INPUTS.get(componentKey);
}

export function resolveValue(
  value: unknown,
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
  options?: ResolveValueOptions,
) {
  const key = 'just-some-key';
  const valuesToResolve: Parameters<typeof resolveValues>[0] = {
    [key]: value,
  };

  const valuesOptions: ResolveValuesOptions = {
    overrideWhitelistCheck: options?.overrideWhitelistCheck ?? false,
  };

  if (options?.errorCallback !== undefined) {
    valuesOptions.errorCallbacks = {
      [key]: options.errorCallback,
    };
  }

  const resolvedValues = resolveValues(
    valuesToResolve,
    dataProvidersResolver,
    componentsRelationshipService,
    idProviderService,
    componentRef,
    valuesOptions,
  );

  return resolvedValues[key];
}

export function resolveValues(
  values: Record<string, unknown>,
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
  options?: ResolveValuesOptions,
): { [key: string]: unknown } {
  const componentKey = (componentRef.instance as DataContainer).__data__
    ?.__componentKey__;
  if (componentKey === undefined) {
    throw new Error(
      'Component key should already have been set on the component. ' +
        'Is resolving values on the data container not the first thing that happens anymore with respect to resolving values?',
    );
  }

  const resolvedValuesContainer: { [key: string]: unknown } = {};

  const dynamicBindableValuesToResolve: {
    [propertyName: string]: IDynamicBindableValue;
  } = {};

  useRelativeProvidersOrListForDataProvidersResolvers(
    values,
    componentKey,
    dataProvidersResolver,
    componentsRelationshipService,
    idProviderService,
    componentRef,
    dynamicBindableValuesToResolve,
    resolvedValuesContainer,
    options,
  );

  resolveWithDataProvidersResolverIfAny(
    dynamicBindableValuesToResolve,
    dataProvidersResolver,
    componentRef,
    resolvedValuesContainer,
  );

  resolveCombinedDynamicBindableValues(
    values,
    dataProvidersResolver,
    componentsRelationshipService,
    idProviderService,
    componentRef,
    resolvedValuesContainer,
    options,
  );

  setupStartWith(values, resolvedValuesContainer);

  applyPairWise(values, resolvedValuesContainer);

  applyFilters(values, resolvedValuesContainer);

  applyAllTransformations(values, resolvedValuesContainer);

  wrapAllWithLoadingState(values, resolvedValuesContainer);

  catchErrors(resolvedValuesContainer, options?.errorCallbacks);

  return resolvedValuesContainer;
}

function setupStartWith(
  values: Record<string, unknown>,
  resolvedValuesContainer: { [key: string]: unknown },
) {
  for (const [key, value] of Object.entries(values)) {
    if (value instanceof EventEmitter) {
      continue;
    }

    if (
      isIDynamicBindableValueOptions(value) &&
      value.startWith !== undefined &&
      resolvedValuesContainer[key] !== undefined
    ) {
      const resolvedValue = resolvedValuesContainer[key];
      if (isObservable(resolvedValue)) {
        resolvedValuesContainer[key] = resolvedValue.pipe(
          startWith(value.startWith.value),
        );
      }
    }
  }
}

function applyFilters(
  values: Record<string, unknown>,
  resolvedValuesContainer: { [key: string]: unknown },
) {
  for (const [key, value] of Object.entries(values)) {
    if (value instanceof EventEmitter) {
      continue;
    }

    if (
      isIDynamicBindableValueOptions(value) &&
      value.filter !== undefined &&
      resolvedValuesContainer[key] !== undefined
    ) {
      const resolvedValue = resolvedValuesContainer[key];
      if (isObservable(resolvedValue)) {
        resolvedValuesContainer[key] = resolvedValue.pipe(
          filter((v) => {
            if (
              typeof value.filter !== 'string' ||
              !value.filter.startsWith('function')
            ) {
              throw new Error(
                'Filter should be a function. Did you forget toString perhaps?',
              );
            }

            const functionBody = `
              const f = ${value.filter};      
              return f(${JSON.stringify(v)});
            `;

            const filterFunction = new Function(functionBody);
            const result = Boolean(filterFunction());
            return result;
          }),
        );
      }
    }
  }
}

function applyPairWise(
  values: Record<string, unknown>,
  resolvedValuesContainer: { [key: string]: unknown },
) {
  for (const [key, value] of Object.entries(values)) {
    if (value instanceof EventEmitter) {
      continue;
    }

    if (
      isIDynamicBindableValueOptions(value) &&
      value.pairWise === true &&
      resolvedValuesContainer[key] !== undefined
    ) {
      const resolvedValue = resolvedValuesContainer[key];
      if (isObservable(resolvedValue)) {
        resolvedValuesContainer[key] = resolvedValue.pipe(pairwise());
      }
    }
  }
}

function resolveWithDataProvidersResolverIfAny(
  dynamicBindableValuesToResolve: {
    [propertyName: string]: IDynamicBindableValue;
  },
  dataProvidersResolver: IDataProvidersResolver,
  componentRef: ComponentRef<unknown>,
  resolvedValuesContainer: { [key: string]: unknown },
) {
  const hasDynamicVBindableValuesToResolve =
    Object.keys(dynamicBindableValuesToResolve).length > 0;

  if (hasDynamicVBindableValuesToResolve) {
    resolveDynamicBindableValuesFromDataProvidersResolver(
      dataProvidersResolver,
      componentRef,
      dynamicBindableValuesToResolve,
      resolvedValuesContainer,
      componentRef.injector,
    );
  }
}

// tslint:disable-next-line:cognitive-complexity
function useRelativeProvidersOrListForDataProvidersResolvers(
  values: Record<string, unknown>,
  componentKey: string,
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
  dynamicBindableValuesToResolve: {
    [propertyName: string]: IDynamicBindableValue;
  },
  resolvedValuesContainer: { [key: string]: unknown },
  options?: ResolveValueOptions,
) {
  for (const propertyName of Object.keys(values)) {
    let value = values[propertyName];

    if (isIDynamicBindableValueCombination(value)) {
      continue;
    }

    let shouldSetResolvedValue = true;
    if (isLocaleMessageId(value)) {
      const localePipe = new DynamicLocalePipe();
      value = localePipe.transform(values[propertyName] as string);
    } else if (isIDynamicBindableValue(value)) {
      const isNested =
        isIDynamicBindableValue(value.property) ||
        isIDynamicBindableValueCombination(value.property);
      if (isNested) {
        const nestedPropertyValue = resolveNestedDynamicBindableValue(
          value,
          dataProvidersResolver,
          componentsRelationshipService,
          idProviderService,
          componentRef,
          options,
        );

        const newValue: IDynamicBindableValue = {
          provider: value.provider,
          property: nestedPropertyValue,
        };
        value = newValue;
      }

      if (!isIDynamicBindableValue(value)) {
        throw new Error(
          'The type of the value should have stayed an IDynamicBindableValue',
        );
      }

      if (isUsingRelativeProvider(value)) {
        value = resolveValueUsingRelativeProvider(
          value,
          componentsRelationshipService,
          componentRef,
          componentKey,
        );
      } else if (isUsingIdProvider(value.provider)) {
        function isIdProviderBindableValue(
          bindableValue: IDynamicBindableValue,
        ): bindableValue is { provider: string; property: string } {
          return (
            typeof bindableValue.provider === 'string' &&
            typeof bindableValue.property === 'string'
          );
        }

        if (!isIdProviderBindableValue(value)) {
          throw new Error(
            'Resolving values by referencing elements by their ids requires `property` to be a string.',
          );
        }

        value = resolveValueUsingIdProvider(
          value,
          idProviderService,
          componentRef,
        );
      } else if (
        componentIsWhiteListedToResolveInputsDirectly(componentKey) ||
        (options?.overrideWhitelistCheck ?? false)
      ) {
        // The white list should be a tempory intermediate solution.
        // When all has been refactored to not resolve inside the setter of @Input()'s (or OnInit or any other way inside the component), but inside this method already,
        // then the white list check is no longer applicable.
        // TIP: To see if all has been resolved, search for in epicuro-next\libs\dynamic-elements for 'dataProvidersResolver'.
        //      It should list all components still resolving themselves.
        dynamicBindableValuesToResolve[propertyName] = value;
        shouldSetResolvedValue = false;
      }
    }

    if (shouldSetResolvedValue) {
      resolvedValuesContainer[propertyName] = value;
    }
  }
}

function resolveNestedDynamicBindableValue(
  value: IDynamicBindableValue,
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
  options?: ResolveValueOptions,
) {
  function localResolveValue(valueToResolve: unknown) {
    return resolveValue(
      valueToResolve,
      dataProvidersResolver,
      componentsRelationshipService,
      idProviderService,
      componentRef,
      options,
    );
  }

  if (
    isIDynamicBindableValue(value.property) &&
    isObservable(value.property.property)
  ) {
    const nestedProvider = value.property.provider;
    const nestedProperty = value.property.property;

    return nestedProperty.pipe(
      map((valueOfProperty) => {
        return localResolveValue({
          provider: nestedProvider,
          property: valueOfProperty,
        });
      }),
    );
  } else {
    return localResolveValue(value.property);
  }
}

function resolveValueUsingRelativeProvider(
  dynamicBindableValue: { provider: RelativeProvider; property: string },
  componentsRelationshipService: ComponentsRelationshipService,
  componentRef: ComponentRef<unknown>,
  componentKey: string,
) {
  const sourceComponentRef = getRelativeSource(
    dynamicBindableValue,
    componentsRelationshipService,
    componentRef,
    componentKey,
  );

  if (sourceComponentRef === undefined) {
    throw new Error(
      'Did not obtain a reference to the source component for resolving a value using a relative provider',
    );
  }

  const resolvedValue = getValueFromSourceWithPropertySubPathIfNeeded(
    componentRef,
    sourceComponentRef,
    dynamicBindableValue,
  );

  return resolvedValue;
}

export function getValueFromSourceWithPropertySubPathIfNeeded(
  componentRef: ComponentRef<unknown>,
  sourceComponentRef: ComponentRef<unknown>,
  dynamicBindableValue: { property: string },
): unknown {
  const valueFromSource = getValueFromSource(
    componentRef,
    sourceComponentRef,
    dynamicBindableValue,
  );

  const resolvedValue = resolveValueWithPropertySubPathIfNeeded(
    valueFromSource,
    dynamicBindableValue,
  );

  return resolvedValue;
}

function resolveValueUsingIdProvider(
  dynamicBindableValue: { provider: string; property: string },
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
) {
  const id = getIdFromProvider(dynamicBindableValue.provider);
  const resolvedValue$ = idProviderService.resolveValue(
    componentRef,
    id,
    dynamicBindableValue.property,
  );

  return resolvedValue$;
}

export function resolveValueWithPropertySubPathIfNeeded(
  valueFromSource: unknown,
  value: { property: string },
) {
  const { propertySubPath } = getPropertyPathInfo(value);

  let valueToSet: unknown;

  if (
    propertySubPath !== undefined &&
    typeof valueFromSource === 'object' &&
    valueFromSource
  ) {
    if (isObservable(valueFromSource)) {
      valueToSet = valueFromSource.pipe(
        map((data) => {
          if (typeof data === 'object' && data) {
            return getValue(data, propertySubPath);
          }

          return data;
        }),
      );
    } else {
      valueToSet = getValue(valueFromSource, propertySubPath);
    }
  } else {
    valueToSet = valueFromSource;
  }

  return valueToSet;
}

function getRelativeSource(
  value: IDynamicBindableValue,
  componentsRelationshipService: ComponentsRelationshipService,
  componentRef: ComponentRef<unknown>,
  componentKey: string,
): ComponentRef<unknown> | undefined {
  let sourceComponentRef: ComponentRef<unknown> | undefined;

  switch (value.provider) {
    case RelativeProvider.parent:
    case RelativeProvider.selfParent:
      const startWithSelf = value.provider === RelativeProvider.selfParent;
      const parent = getParent(
        componentsRelationshipService,
        componentRef,
        componentKey,
        startWithSelf,
      );
      const parentComponentRef =
        componentsRelationshipService.getComponentRef(parent);
      sourceComponentRef = parentComponentRef;
      break;
    case RelativeProvider.self:
      sourceComponentRef = componentRef;
      break;
    default:
      throw new Error(
        `Was this relative provider '${value.provider}' added recently? As it seems be unhandled.`,
      );
  }

  return sourceComponentRef;
}

export function isLocaleMessageId(itemValue: unknown) {
  return typeof itemValue === 'string' && LOCALE_PREFIX_REGEXP.test(itemValue);
}

export function takeUntilSourceComponentIsDestroyed(
  componentRef: ComponentRef<unknown>,
  valueFromSource: Observable<unknown>,
  sourceComponentRef: ComponentRef<unknown>,
): Observable<unknown> {
  const isDestroyed$ = new Subject<void>();

  const onDestroy = () => {
    isDestroyed$.next();
    isDestroyed$.complete();
  };

  componentRef.onDestroy(() => {
    onDestroy();
  });

  if (sourceComponentRef !== componentRef) {
    sourceComponentRef.onDestroy(() => {
      onDestroy();
    });
  }

  return valueFromSource.pipe(takeUntil(isDestroyed$));
}

export function resolveCombinedDynamicBindableValues(
  values: Record<string, unknown>,
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
  resolvedValuesContainer: { [key: string]: unknown },
  options?: ResolveValuesOptions,
) {
  const combinedDynamicBindableValuesToResolve: Record<
    string,
    IDynamicBindableValueCombination
  > = {};

  for (const [key, value] of Object.entries(values)) {
    if (isIDynamicBindableValueCombination(value)) {
      combinedDynamicBindableValuesToResolve[key] = value;
    }
  }

  for (const [key, value] of Object.entries(
    combinedDynamicBindableValuesToResolve,
  )) {
    const dynamicBindableValuesToResolve: Record<
      string,
      IDynamicBindableValue | IDynamicBindableValueCombination
    > = {};
    const orderOfKeys = [];
    for (let i = 0; i < value.values.length; i++) {
      const valueToCombineKey = `value-to-combine[${i}]`;
      dynamicBindableValuesToResolve[valueToCombineKey] = value.values[i];
      orderOfKeys.push(valueToCombineKey);
    }

    const resolvedValues = resolveValues(
      dynamicBindableValuesToResolve,
      dataProvidersResolver,
      componentsRelationshipService,
      idProviderService,
      componentRef,
      options,
    );

    const entries = Object.entries(resolvedValues);
    const entriesSorted = orderOfKeys.map((keyInOrder) => {
      const entry = entries.find(([keyOfEntry]) => keyOfEntry === keyInOrder);
      if (entry === undefined) {
        throw new Error(`No entry found by key '${keyInOrder}'.`);
      }
      return entry;
    });

    const resolvedValuesArray: Observable<unknown>[] = entriesSorted.map(
      ([, resolvedValue]) => {
        if (!isObservable(resolvedValue)) {
          resolvedValue = of(resolvedValue);
        }

        return resolvedValue as Observable<unknown>;
      },
    );

    resolvedValuesContainer[key] = combineLatest(resolvedValuesArray);
  }
}

function getParent(
  componentsRelationshipService: ComponentsRelationshipService,
  componentRef: ComponentRef<unknown>,
  componentKey: string,
  startWithSelf: boolean,
): unknown {
  let parent: unknown;
  if (startWithSelf) {
    parent = componentRef.instance;
  } else {
    parent = componentsRelationshipService.getParent(componentRef.instance);
  }

  while (parent !== undefined && isCollectionRendererWithoutData(parent)) {
    parent = componentsRelationshipService.getParent(parent);
  }

  if (!parent) {
    console.error('Parent not found', { componentRef });
    throw new Error(
      `Couldn't find parent for component with key ${componentKey}. Please make sure that parent component is registered before child component`,
    );
  }

  return parent;
}

function isCollectionRendererWithoutData(element: unknown): boolean {
  const instance = element as DataContainer & { name?: string };
  const keyToFilterOut: keyof HasComponentKeyType = '__componentKey__';
  const keys = Object.keys(instance.__data__ ?? {}).filter(
    (k) => k !== keyToFilterOut,
  );
  return instance.name === dynamicCollectionRendererKey && keys.length === 0;
}

export function getValueFromSource(
  componentRef: ComponentRef<unknown>,
  sourceComponentRef: ComponentRef<unknown>,
  value: { property: string },
): unknown {
  const sourceInstance = sourceComponentRef.instance;
  const dataContainer = (sourceInstance as DataContainer).__data__;

  const { propertyName } = getPropertyPathInfo(value);
  const valueFromSource =
    dataContainer?.[propertyName] !== undefined
      ? dataContainer?.[propertyName]
      : (sourceInstance as { [key: string]: unknown })[propertyName];
  if (valueFromSource === undefined) {
    throw new Error(
      `Couldn't find property ${value.property} in source component`,
    );
  }

  let valueToReturn: unknown = valueFromSource;

  if (isObservable(valueToReturn)) {
    valueToReturn = takeUntilSourceComponentIsDestroyed(
      componentRef,
      valueToReturn,
      sourceComponentRef,
    );
  }

  return valueToReturn;
}

function getPropertyPathInfo(value: { property: string }) {
  const propertyPath = value.property as string;
  const indexOfDot = propertyPath.indexOf('.');
  const containsSubPath = indexOfDot !== -1;
  const propertyName = containsSubPath
    ? propertyPath.substring(0, indexOfDot)
    : propertyPath;
  const propertySubPath = containsSubPath
    ? propertyPath.substring(indexOfDot + 1)
    : undefined;
  return { propertyName, propertySubPath };
}

export function applyTransformation(
  value: unknown,
  transform: string,
): Observable<unknown> {
  if (isObservable(value)) {
    return value.pipe(
      mergeMap((valueFromObservable) => {
        return applyTransformation(valueFromObservable, transform);
      }),
    );
  }

  const dataString = JSON.stringify(value);

  if (typeof transform === 'function') {
    throw new Error(
      `The transform for '${value}' is a function and a string is expected.`,
    );
  }

  transform = transform.trim();
  const hasArrowFunction =
    transform.startsWith('(') &&
    transform.indexOf(')') !== -1 &&
    transform.indexOf('=>') !== -1;

  let functionBody: string;
  if (hasArrowFunction || transform.startsWith('function')) {
    functionBody = `
        const f = ${transform};      
        return f(${dataString});
      `;
  } else {
    functionBody = `
      let data = ${dataString};
      ${transform.trim()}
      return data;
    `;
  }

  const func = new Function(functionBody);
  const result = func();
  return of(result);
}

function applyAllTransformations(
  values: Record<string, unknown>,
  resolvedValuesContainer: { [key: string]: unknown },
) {
  for (const [key, value] of Object.entries(values)) {
    if (value instanceof EventEmitter) {
      continue;
    }

    if (
      isIDynamicBindableValueOptions(value) &&
      value.transform !== undefined &&
      resolvedValuesContainer[key] !== undefined
    ) {
      const resolvedValue = resolvedValuesContainer[key];
      const transformedValue = applyTransformation(
        resolvedValue,
        value.transform,
      );
      resolvedValuesContainer[key] = transformedValue;
    }
  }
}

export function wrapWithLoadingState(
  value: Observable<unknown>,
): Observable<{ isLoading: boolean; data?: unknown }> {
  return concat(
    of({ isLoading: true }),
    value.pipe(
      map((data) => {
        return { isLoading: false, data };
      }),
    ),
  );
}

export function wrapAllWithLoadingState(
  values: Record<string, unknown>,
  resolvedValuesContainer: { [key: string]: unknown },
) {
  for (const [key, value] of Object.entries(values)) {
    if (value instanceof EventEmitter) {
      continue;
    }

    const resolvedValue = resolvedValuesContainer[key];
    if (
      isIDynamicBindableValueOptions(value) &&
      value.wrapWithLoadingState &&
      isObservable(resolvedValue)
    ) {
      const transformedValue = wrapWithLoadingState(resolvedValue);
      resolvedValuesContainer[key] = transformedValue;
    }
  }
}

export function catchErrors(
  resolvedValuesContainer: {
    [key: string]: unknown;
  },
  errorCallbacks?: ErrorCallbacks,
): void {
  for (const [key, value] of Object.entries(resolvedValuesContainer)) {
    if (value instanceof EventEmitter) {
      continue;
    }

    if (isObservable(value)) {
      const transformedValue = value.pipe(
        catchError((error) => {
          console.error(`Error caught while resolving for '${key}'.`, error);
          const errorContainer: ErrorContainer = {
            __resolveValueError__: error.toString(),
          };

          if (errorCallbacks && errorCallbacks[key]) {
            errorCallbacks[key]({ __resolveValueError__: error });
          }

          return of(errorContainer);
        }),
      );
      resolvedValuesContainer[key] = transformedValue;
    }
  }
}
